import React, { useCallback, useEffect, useMemo, useRef } from "react";
import TableComponent from "../TableComponent";
import TableActionButton from "../TableComponent/TableActionButton";
import EyeIcon from "@assets/img/eye-icon-yellow.svg";
import EditIcon from "@assets/img/edit-icon.svg";
import TrashIcon from "@assets/img/trash-icon.svg";
import ChangeToReadyIcon from "@assets/img/change-to-ready-icon.svg";
import styled from "styled-components";
import { useSettings } from "@src/context/SettingsContext";
import { formatDateUS, formatTime } from "@src/utils/formatDate";
import { IPlaylistRecord, PlaylistStatus } from "@src/types/playlist";
import LargeTextTableCell from "../LargeTextTableCell";
import Tooltip from "../Tooltip";
import ArtWorkImage from "../ArtWorkImage";
import ArtWorkImageHoverPreview from "../ArtWorkImageHoverPreview";
import { useFormattingSeconds } from "@src/hooks/useFormattingSeconds";
import RenderContentStatus from "../RenderContentStatus";
import {
  FetchNextPageOptions,
  InfiniteData,
  InfiniteQueryObserverResult,
} from "@tanstack/react-query";
import { useAppDispatch } from "@src/redux/hooks";
import { resetSort, setSort } from "@src/redux/slices/playlistSlice";

import { TableActionsGroup } from "../TableComponent/TableComponent.styles";
import { device } from "@src/theme";
import CellWithTooltip from "../CellWithTooltip";
import { ITrack } from "@src/types/track";
import { ICurator } from "@src/types/curator";
import { useAuthCurator, useRole } from "@src/hooks/useAuth";
import { getPlaylistCoverUrl } from "@src/utils/mediaUrl";
import { FEATURES } from "@src/constants";

const StatusBadgeStyled = styled.div<{ $status: PlaylistStatus }>`
  background-color: ${(props): string =>
    props.$status === "scheduled" ||
    props.$status === "archived" ||
    props.$status === "aired"
      ? props.theme.colors.yellow
      : props.$status === "draft"
        ? props.theme.colors.black
        : props.theme.colors.darkBg};
  border: ${(props): string =>
    props.$status === "scheduled" ||
    props.$status === "archived" ||
    props.$status === "aired"
      ? `1px solid ${props.theme.colors.yellow}`
      : props.$status === "draft"
        ? `1px solid ${props.theme.colors.gray}`
        : `1px solid ${props.theme.colors.yellow}`};
  border-radius: 6px;
  padding: 8px 15px;
  margin-right: ${(props) => props.theme.margins.medium};
  text-align: center;
  max-width: 150px;
  max-height: 28px;
  color: ${(props): string =>
    props.$status === "scheduled" ||
    props.$status === "archived" ||
    props.$status === "aired"
      ? props.theme.colors.black
      : props.$status === "draft"
        ? props.theme.colors.gray
        : props.theme.colors.yellow};
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  text-transform: capitalize;
  user-select: none;
  @media ${device.mobile} {
    padding: 5px 10px;
    font-size: 12px;
  }
  @media ${device.tablet} {
    padding: 5px 10px;
    font-size: 12px;
  }
  @media ${device.laptop} {
    padding: 8px 10px;
    font-size: 14px;
  }
  @media ${device.desktop} {
    padding: 8px 15px;
    font-size: 16px;
  }
`;
const CuratorCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const GuestBadge = styled.span`
  border: 1px solid ${(props) => props.theme.colors.yellow};
  color: ${(props) => props.theme.colors.yellow};
  border-radius: 6px;
  padding: 2px 8px;
  font-size: 12px;
  line-height: 1;
  white-space: nowrap;
  text-transform: uppercase;
  user-select: none;
`;

const StatusBadge = ({
  status,
  tooltipContent,
}: {
  status: PlaylistStatus;
  tooltipContent?: React.ReactNode;
}): React.JSX.Element => {
  const hasTooltip = Boolean(tooltipContent);
  return (
    <StatusBadgeStyled $status={status}>
      {hasTooltip ? (
        <Tooltip content={tooltipContent} position="top">
          {status}
        </Tooltip>
      ) : (
        status
      )}
    </StatusBadgeStyled>
  );
};

type PlaylistsListProps = {
  onRowClick: (row: IPlaylistRecord) => void;
  onEditClick: (row: IPlaylistRecord | null) => void;
  onDeleteClick: (row: IPlaylistRecord) => void;
  onChangeToReadyClick: (row: IPlaylistRecord) => void;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  fetchNextPage: (
    options?: FetchNextPageOptions
  ) => Promise<
    InfiniteQueryObserverResult<InfiniteData<unknown, unknown>, Error>
  >;
  fetchingPlaylists: boolean;
  playlists: IPlaylistRecord[];
  isMoodFactory?: boolean;
};
const PlaylistsList: React.FC<PlaylistsListProps> = ({
  fetchingPlaylists,
  isFetchingNextPage,
  fetchNextPage,
  hasNextPage,
  onRowClick,
  onEditClick,
  onDeleteClick,
  onChangeToReadyClick,
  playlists,
  isMoodFactory,
}) => {
  const dispatch = useAppDispatch();
  const role = useRole();
  const canChangeToReady =
    (role === "admin" || role === "manager") &&
    FEATURES.CHANGE_TO_READY_ENABLED;
  const { documentId: myCuratorDocumentId, isSubcurator } = useAuthCurator();
  const {
    tableSettings: { playlistsTable: compactUI },
  } = useSettings();
  const columns = useMemo(
    () => [
      {
        Header: "#",
        accessor: "display_row_index",
        width: "40px",
      },
      {
        Header: "ID",
        accessor: "id",
        enableSort: true,
        width: "40px",
      },
      {
        hidden: compactUI,
        Header: "",
        accessor: "coverDesktop",
        centerContent: true,
        Cell: ({ row }: { row: any }): React.JSX.Element => (
          <ArtWorkImageHoverPreview
            $compressedUI={compactUI}
            value={getPlaylistCoverUrl(row)}
            alt={row?.title ? `${row.title} cover` : "Playlist cover"}
          />
        ),
      },
      {
        Header: isMoodFactory ? "Mood" : "Show",
        accessor: isMoodFactory ? "moodFactory" : "show",
        Cell: ({ value }: { value: { title: string } }): React.JSX.Element => (
          <CellWithTooltip value={value?.title ?? ""} />
        ),
      },
      {
        Header: "Playlist Name",
        accessor: "title",
        enableSort: true,
        Cell: ({ value }: { value: string }): React.JSX.Element => (
          <CellWithTooltip value={value} />
        ),
      },
      {
        Header: "Description",
        accessor: "overwriteShowDescription",
        Cell: ({ value }: { value: string }): React.JSX.Element => (
          <>
            <Tooltip content={value}>
              <LargeTextTableCell $compressedUI={compactUI} value={value} />
            </Tooltip>
          </>
        ),
      },
      {
        Header: "Last modified",
        accessor: "updatedAt",
        Cell: ({ value }: { value: string }): React.JSX.Element => (
          <Tooltip
            content={
              <>
                {formatTime(new Date(value))} {formatDateUS(new Date(value))}
              </>
            }
          >
            <span>
              {value ? (
                <>
                  {formatTime(new Date(value))} <br />{" "}
                  {formatDateUS(new Date(value))}
                </>
              ) : (
                ""
              )}
            </span>
          </Tooltip>
        ),
        enableSort: true,
      },
      {
        Header: "# of Tracks",
        accessor: "tracks",
        Cell: ({ value }: { value: any[] }): React.JSX.Element => (
          <Tooltip content={value?.length}>
            <span>{value?.length ?? ""}</span>
          </Tooltip>
        ),
      },
      {
        Header: "Duration",
        accessor: "tracksDuration",
        Cell: ({
          row,
        }: {
          value: number;
          row: IPlaylistRecord;
        }): React.JSX.Element => {
          const totalDuration =
            row.tracks?.reduce((acc: number, track: ITrack) => {
              return acc + (track.duration ?? 0);
            }, 0) ?? 0;
          if (totalDuration) {
            const { formatted } = useFormattingSeconds(totalDuration);
            return <span>{formatted}</span>;
          }
          return <span>00:00:00</span>;
        },
      },
      {
        Header: role === "admin" || role === "manager" ? "Curator" : "Guest",
        accessor: "curator",
        hidden: isSubcurator && role !== "admin" && role !== "manager",
        Cell: ({
          value,
          row,
        }: {
          value: Partial<ICurator>;
          row: IPlaylistRecord;
        }): React.JSX.Element => {
          const mainCuratorDocumentId = row?.show?.curators?.[0]?.documentId;
          const isNotMainCurator = Boolean(
            row?.curator?.documentId &&
            mainCuratorDocumentId &&
            row.curator.documentId !== mainCuratorDocumentId
          );
          const isOwnPlaylist =
            myCuratorDocumentId === row?.curator?.documentId;
          const curatorName = isOwnPlaylist ? "" : value?.name || "";

          if (!curatorName && !isNotMainCurator) return <span></span>;

          const guestBadge = isNotMainCurator ? (
            <Tooltip content="Not the main curator on this show">
              <GuestBadge>Guest</GuestBadge>
            </Tooltip>
          ) : null;

          return (
            <CuratorCell>
              {curatorName ? (
                <Tooltip
                  content={
                    <ArtWorkImage
                      $compressedUI={compactUI}
                      value={curatorName}
                    />
                  }
                >
                  <span>{curatorName}</span>
                </Tooltip>
              ) : null}
              {guestBadge}
            </CuratorCell>
          );
        },
      },
      {
        Header: "Status",
        accessor: "playlistStatus",
        textOverflow: "initial",
        Cell: ({
          value,
          row,
        }: {
          value: PlaylistStatus;
          row: IPlaylistRecord;
        }): React.JSX.Element => {
          let tooltipContent: React.ReactNode | undefined;

          if (value === "archived" || value === "aired") {
            // For archived and aired playlists, show "Aired" + date aired (releaseDate) if available
            if (row.releaseDate) {
              const airedDate = new Date(row.releaseDate);
              tooltipContent = (
                <>
                  Aired on: {formatTime(airedDate)} {formatDateUS(airedDate)}
                </>
              );
            } else {
              tooltipContent = undefined;
            }
          } else {
            switch (value) {
              case "scheduled":
                tooltipContent = "Scheduled for";
                break;
              case "draft":
                tooltipContent = "This playlist is under 2 hours 4 minutes";
                break;
              case "ready":
                tooltipContent = "Ready";
                break;
              case "submitted":
                tooltipContent = "Submitted";
                break;
              default:
                break;
            }
          }

          return <StatusBadge tooltipContent={tooltipContent} status={value} />;
        },
        enableSort: true,
      },
      {
        Header: "Notes",
        accessor: "notes",
        Cell: ({ value }: { value: string }): React.JSX.Element => (
          <LargeTextTableCell $compressedUI={compactUI} value={value} />
        ),
      },
      {
        Header: "Actions",
        accessor: "actions",
        Cell: ({ row }: { row: IPlaylistRecord }): React.JSX.Element => (
          <>
            <TableActionsGroup>
              {canChangeToReady && row.playlistStatus === "submitted" && (
                <TableActionButton
                  $tooltipContent="Change to Ready"
                  className="hover-scale"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChangeToReadyClick(row);
                  }}
                  alt="change to ready"
                  iconSrc={ChangeToReadyIcon}
                />
              )}
              <TableActionButton
                $tooltipContent="View Playlist"
                className="hover-scale"
                onClick={() => onRowClick(row)}
                alt="view"
                iconSrc={EyeIcon}
              />
              <TableActionButton
                $tooltipContent="Edit Playlist"
                className="hover-scale"
                onClick={(e) => {
                  e.stopPropagation();
                  onEditClick(row);
                }}
                alt="edit"
                iconSrc={EditIcon}
              />
              {/* Disable only for status that are not draft or ready */}
              <TableActionButton
                disabled={
                  row.playlistStatus !== "draft" &&
                  row.playlistStatus !== "ready"
                }
                $tooltipContent="Delete Playlist"
                className="hover-scale"
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteClick(row);
                }}
                alt="remove"
                iconSrc={TrashIcon}
              />
            </TableActionsGroup>
          </>
        ),
      },
    ],
    [compactUI, onEditClick, canChangeToReady, onChangeToReadyClick]
  );
  const tableEndRef = useRef<HTMLTableRowElement | null>(null);

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const target = entries[0];
      if (target.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage]
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: "0px",
      threshold: 0.1,
    });

    if (tableEndRef.current) observer.observe(tableEndRef.current);

    return () => {
      if (tableEndRef.current) observer.unobserve(tableEndRef.current);
    };
  }, [handleObserver]);

  const handleSort = (sortData: {
    sort: "" | "asc" | "desc";
    accessor: string;
  }) => {
    if (sortData.sort === "") {
      return dispatch(resetSort({ role }));
    }
    dispatch(
      setSort({
        direction: sortData.sort,
        field: sortData.accessor,
      })
    );
  };

  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <TableComponent
        onSortCb={handleSort}
        loaderRef={tableEndRef}
        $compactUI={compactUI}
        onRowClick={onRowClick}
        columns={columns}
        data={playlists}
        loadingNextPage={isFetchingNextPage}
      />
      <RenderContentStatus
        loading={fetchingPlaylists}
        noResultsFoundMessage={
          !fetchingPlaylists && playlists.length === 0 ? "Add a playlist" : ""
        }
        results={playlists.length}
      />
    </div>
  );
};

export default PlaylistsList;
